import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Portfolio, ProductRow } from '../lib/contract';
import { useResourceMapped } from '../lib/api';
import { mapPortfolio, type ApiPortfolio } from '../lib/adapt';
import { useSession } from '../lib/session';
import { useMembers } from '../lib/members';
import { Badge, Btn, PageHead } from '../components/ui/primitives';
import { Loading } from '../components/ui/widgets';



const PHASE_BADGE: Record<ProductRow['phase'], { kind: 'safe' | 'out' | 'med'; label: string }> = {
  active: { kind: 'safe', label: 'Active' },
  not_released: { kind: 'out', label: 'Not released' },
  eol: { kind: 'med', label: 'EOL' },
  retired: { kind: 'out', label: 'Retired' },
};

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

function OwnerCell({ ownerUserRef, memberName }: { ownerUserRef: string | null; memberName: Record<string, string> }) {
  const name = ownerUserRef ? memberName[ownerUserRef] : undefined;
  if (!name) return <Badge kind="out">unassigned</Badge>;
  return (
    <span className="owner">
      <span className="ava-sm">{initials(name)}</span>
      {name}
    </span>
  );
}

function FindingsCell({ openFindings, art14Open }: { openFindings: number; art14Open: number }) {
  if (art14Open > 0) return <Badge kind="crit">{`${openFindings} · ${art14Open} Art.14`}</Badge>;
  if (openFindings > 0) return <Badge kind="warn">{`${openFindings} findings`}</Badge>;
  return <Badge kind="safe">[✓] clean</Badge>;
}

export function Products() {
  const { session } = useSession();
  const navigate = useNavigate();
  const { data, loading } = useResourceMapped<ApiPortfolio, Portfolio>(session, '/api/portfolio', mapPortfolio);
  const { members } = useMembers(session.organizationId);
  const memberName: Record<string, string> = Object.fromEntries(members.map((m) => [m.id, m.name]));
  const [query, setQuery] = useState('');
  const [chip, setChip] = useState<'all' | 'findings' | 'art14' | 'clean'>('all');

  const allProducts = data?.products ?? [];
  const products = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allProducts.filter((p) => {
      if (chip === 'findings' && !(p.openFindings > 0)) return false;
      if (chip === 'art14' && !(p.art14Open > 0)) return false;
      if (chip === 'clean' && (p.openFindings > 0 || p.art14Open > 0)) return false;
      if (q && ![p.name, ...p.versions.map((v) => v.label), ...p.markets].some((v) => v?.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allProducts, query, chip]);

  if (loading || !data) return <Loading />;

  const chips: Array<{ k: typeof chip; label: string }> = [
    { k: 'all', label: 'All' }, { k: 'findings', label: 'With findings' },
    { k: 'art14', label: 'Art.14' }, { k: 'clean', label: 'Clean' },
  ];

  return (
    <>
      <PageHead
        title="Products"
        sub={`${products.length} products tracked across your EU single-market portfolio.`}
        action={
          <Btn variant="primary" onClick={() => navigate('/app/products/new')}>
            ＋ New product
          </Btn>
        }
      />

      <div className="filters">
        <div className="search">
          <span>⌕</span>
          <input placeholder="Search products, versions, markets…" data-testid="product-search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {chips.map((c) => (
          <button key={c.k} className={`chip${chip === c.k ? ' on' : ''}`} data-testid={`product-chip-${c.k}`} onClick={() => setChip(c.k)}>{c.label}</button>
        ))}
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Version</th>
              <th>Owner</th>
              <th>Phase</th>
              <th>Markets</th>
              <th>Findings</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const phase = PHASE_BADGE[p.phase];
              return (
                <tr key={p.id} className="clk" onClick={() => navigate(`/app/products/${p.id}`)}>
                  <td>
                    <b>{p.name}</b>
                  </td>
                  <td className="mono">{p.versions[0]?.label ?? '—'}</td>
                  <td>
                    <OwnerCell ownerUserRef={p.ownerUserRef} memberName={memberName} />
                  </td>
                  <td>
                    <Badge kind={phase.kind}>{phase.label}</Badge>
                  </td>
                  <td className="mono">{p.markets.join(' ') || '—'}</td>
                  <td>
                    <FindingsCell openFindings={p.openFindings} art14Open={p.art14Open} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
